import { useEffect, useRef, useState } from 'react';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { getUserInfo } from '@marriott/mi-store-utils';

interface UseGetUserInfoOptions {
  sessionToken: string | undefined;
  requestId: string | undefined;
  currentLocale: string | undefined;
}

interface UseGetUserInfoResult {
  hasSettled: boolean;
}

/**
 * Calls getUserInfo once per mount as soon as a session token is available.
 * A ref guard prevents duplicate calls even when sessionData, requestId, or
 * currentLocale change after the initial invocation (e.g. after a client-side
 * session refresh), which avoids infinite re-render loops.
 */
export function useGetUserInfo({
  sessionToken,
  requestId,
  currentLocale,
}: UseGetUserInfoOptions): UseGetUserInfoResult {
  const calledRef = useRef(false);
  const [hasSettled, setHasSettled] = useState(false);

  useEffect(() => {
    const userId = document?.cookie?.split('UserIdToken=')[1]?.split(';')[0];

    if (!sessionToken || calledRef.current) return;
    calledRef.current = true;

    void getUserInfo(false, {
      'x-request-id': requestId,
      'accept-language': currentLocale || '',
      Cookie: `sessionID=${sessionToken};UserIdToken=${userId || ''};`,
    })
      .then(() => {
        setHasSettled(true);
      })
      .catch(() => {
        setHasSettled(true);
      });
  }, [sessionToken, requestId, currentLocale, getUserInfo]);

  return { hasSettled };
}
